import { useAccess } from '../lib/access.jsx'
import styles from './ActingAsBanner.module.css'

/**
 * Persistent "acting as" banner. Renders above everything, on every route, for
 * as long as a user holds control of an account — impersonation must never be
 * a state you can forget you are in.
 *
 * It also tells the truth about how the session was granted: until the backend
 * exposes the authorization route, control is client-side only and the banner
 * says so rather than implying an approval that never happened.
 */
export default function ActingAsBanner() {
  const { actingAs, stopActingAs, roleMeta, audit } = useAccess()
  if (!actingAs) return null

  const label = actingAs.name || actingAs.id || 'account'
  const unverified = actingAs.serverGranted === false

  return (
    <div className={styles.banner} role="status" data-unverified={unverified}>
      <span className={styles.pulse} aria-hidden="true" />

      <div className={styles.text}>
        <span className={styles.eyebrow}>Acting as</span>
        <strong className={styles.name}>{label}</strong>
        <span className={styles.sep}>·</span>
        <span className={styles.role}>{roleMeta?.label}</span>
        {unverified && (
          <span
            className={styles.warn}
            title="The backend authorization route (/api/access/acting-as) is not deployed yet, so this control session was not approved server-side."
          >
            not server-verified
          </span>
        )}
        {audit?.sink === false && (
          <span
            className={styles.warn}
            title="POST /api/audit is not deployed. Actions are held in this tab only and are lost when it closes."
          >
            audit not persisted
          </span>
        )}
      </div>

      <button className={styles.exit} onClick={stopActingAs}>
        Exit
      </button>
    </div>
  )
}
